/* eslint-disable import/no-named-as-default-member */
import React, { createContext, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

import Tracking from '@spotahome/soyuz-tracking';

import SoyuzLocalStorage from '@spotahome/marketplace-common/src/utils/soyuzLocalStorage';

import './HotspotsContext.scss';

const HotspotsContext = createContext();

export const HOTSPOT_FILTERS = {
  availabilities: 'availabilities',
  propertyType: 'propertyType',
  budget: 'budget'
};

const HOTSPOT_ORDER = [
  HOTSPOT_FILTERS.availabilities,
  HOTSPOT_FILTERS.propertyType,
  HOTSPOT_FILTERS.budget
];

const HOTSPOT_LOCAL_STORAGE = 'sah_hotspots_visited';
const HOTSPOT_FILTER_LOCAL_STORAGE = 'sah_hotspots_filter_visited';

const getStoredItems = storage => () => {
  const visitedFiltersStored = SoyuzLocalStorage.getItem(storage);

  return visitedFiltersStored ? JSON.parse(visitedFiltersStored) : [];
};

const getStoredHotspots = getStoredItems(HOTSPOT_LOCAL_STORAGE);
const getStoredVisitedFilters = getStoredItems(HOTSPOT_FILTER_LOCAL_STORAGE);

const storeItemLocalStorage = storage => item => {
  const storedItems = getStoredItems(storage)();
  SoyuzLocalStorage.setItem(storage, JSON.stringify([...storedItems, item]));
};

const storeHotspot = storeItemLocalStorage(HOTSPOT_LOCAL_STORAGE);
const storeVisitedFilter = storeItemLocalStorage(HOTSPOT_FILTER_LOCAL_STORAGE);

export const HotspotsProvider = ({ children }) => {
  const [activeHotspot, setActiveHotspot] = useState(null);

  const activateNextHotspot = lastVisited => {
    const lastVisitedIndex = HOTSPOT_ORDER.indexOf(lastVisited);
    const nextActiveHotspot = HOTSPOT_ORDER[lastVisitedIndex + 1];

    if (getStoredVisitedFilters().includes(nextActiveHotspot)) {
      activateNextHotspot(nextActiveHotspot);
    } else {
      setActiveHotspot(nextActiveHotspot);
    }
  };

  useEffect(() => {
    const visitedHotspots = getStoredHotspots();
    const lastVisited = visitedHotspots.pop();

    activateNextHotspot(lastVisited);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setHotspotVisited = hotspotName => () => {
    const hotspotIndex = HOTSPOT_ORDER.indexOf(hotspotName) + 1;
    Tracking.ga.sendEvent('Search', 'Wizard', `Step${hotspotIndex}`);

    if (activeHotspot === hotspotName) {
      storeHotspot(hotspotName);
      activateNextHotspot(hotspotName);
    } else {
      storeVisitedFilter(hotspotName);
    }
  };

  const isHotspotActive = hotspotName => () => activeHotspot === hotspotName;

  return (
    <HotspotsContext.Provider
      value={{
        setHotspotVisited,
        isHotspotActive
      }}
    >
      {children}
    </HotspotsContext.Provider>
  );
};

HotspotsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

const useHotspotsContext = () => useContext(HotspotsContext);

const HotspotLed = () => <span className="hotspots-led" />;

export const useHotspotItem = hotspotName => {
  const context = useHotspotsContext();

  return {
    setHotspotVisited: context.setHotspotVisited(hotspotName),
    isHotspotActive: context.isHotspotActive(hotspotName),
    renderHotspotLed: () =>
      context.isHotspotActive(hotspotName)() ? (
        <HotspotLed hotspotName={hotspotName} />
      ) : null
  };
};

export default {
  HotspotsProvider,
  useHotspotItem,
  HOTSPOT_FILTERS
};
